import { enumCodesOf } from './enumCodes'

const codeOf = (value) => (value && typeof value.code === 'string' ? value.code : '')
const nameOf = (value) => (value && typeof value.name === 'string' ? value.name : '')

/**
 * Story 204 — the ONE rule that turns a system value into the user's text (02-reglas.md RN1.2, RN2.1, P9).
 *
 * A string is an enum: translated by its code, the code itself as fallback (RN1.2). An object `{code, name, systemDefault?}`
 * is a status or reference row: a row the customer created (`systemDefault === false`) is shown by its `name` verbatim
 * (RN-T1); any other row is Sun's and is translated by its code, its `name` (the factory text) as fallback when no language
 * has a key for it (RN-T3). A row without a code is translated by its `name` as a literal key: only a screen that knows it
 * paints seeded data calls this helper by hand (P17: `progress_calculation_type`, the type of an order status, a worker's
 * calendar…); the generic table never guesses a row without a code (tableSystemValues.js). A seeded row cannot be renamed
 * in MIC (`code` and `name` are read-only when `systemDefault`), so there is nothing else to tell apart and Jupiter needs
 * no copy of Sun's catalogue (P9).
 *
 * The casing follows develop (P15, `matchCase`): a row reads with the casing of its `name`, an enum with the dictionary's.
 *
 * `labelKeys` carries only the legacy keys of the codes whose text still differs between screens, until the PO picks one
 * (RN1.5, P1-bis); every other code is asked by its own key. Never returns null/undefined: '' when there is nothing to show.
 *
 * @param {function(string, Object=): string} t_
 * @param {string} domain Table name (STATUS/REFERENCE) or Java enum name (ENUM), kept so the call site says what it shows
 * @param {string|{code?: (string|null), name?: string, systemDefault?: boolean}} value
 * @param {Object<string, string>} [labelKeys]
 * @returns {string}
 */
export const systemValueLabel = (t_, domain, value, labelKeys = {}) => {
  if (typeof value === 'string') {
    return value === '' ? '' : translated(t_, labelKeys[value] || value, value)
  }
  const code = codeOf(value)
  const name = nameOf(value)
  if (!value || value.systemDefault === false) return name
  if (code === '') return name === '' ? '' : matchCase(translated(t_, name, name), name)
  const factoryText = name !== '' ? name : code
  return matchCase(translated(t_, labelKeys[code] || code, factoryText), factoryText)
}

/**
 * P19: an enum that develop painted as its raw code reads translated with the code's casing (`AUTOMATIC` ⇒ `AUTOMÁTICO`),
 * whatever the casing of its key in the dictionary.
 *
 * @param {function(string, Object=): string} t_
 * @param {string} domain
 * @param {string} code
 * @param {Object<string, string>} [labelKeys]
 * @returns {string}
 */
export const systemValueCodeLabel = (t_, domain, code, labelKeys = {}) =>
  typeof code === 'string' && code !== '' ? matchCase(systemValueLabel(t_, domain, code, labelKeys), code) : (code ?? '')

const translated = (t_, key, defaultValue) => t_(key, { defaultValue })

const hasLetters = (text) => text.toLocaleUpperCase() !== text.toLocaleLowerCase()
const isAllCaps = (text) => hasLetters(text) && text === text.toLocaleUpperCase()

/**
 * P15 (PO): develop's look, translated. A row is shown with the casing of the text Sun sends in its place (`name`, or the
 * code): `DRILL` ⇒ `TALADRADO`, `Drill` ⇒ `Taladrado` even when the dictionary key is a legacy one in capitals (RN1.12).
 * An enum keeps the dictionary's casing (develop painted the legacy key, or the code in capitals), so it never comes here.
 */
export const matchCase = (text, source) => {
  if (isAllCaps(source)) return text.toLocaleUpperCase()
  if (isAllCaps(text) && hasLetters(source)) return text[0] + text.slice(1).toLocaleLowerCase()
  return text
}

/**
 * P10: the description of a status or reference row. A customer row keeps its text; a seeded row's description is factory
 * text too: when it repeats the name (the usual case) it reads as the name does, otherwise by its own literal key (RN-T3).
 *
 * @param {function(string, Object=): string} t_
 * @param {string} domain
 * @param {{code?: string, name?: string, description?: string, systemDefault?: boolean}} row
 * @returns {string}
 */
export const systemValueDescription = (t_, domain, row) => {
  const description = row && typeof row.description === 'string' ? row.description : ''
  if (description === '' || !row || row.systemDefault === false) return description
  if (description === nameOf(row)) return systemValueLabel(t_, domain, row)
  return matchCase(translated(t_, description, description), description)
}

/** RN1.9: the options of a label filter (`filterByLabel`) over an exposed enum; the value is the code Sun filters by today. */
export const enumFilterOptions = (t_, domain) =>
  enumCodesOf(domain).map((code) => ({ value: code, label: systemValueLabel(t_, domain, code) }))

/** P19: the same filter over an enum that develop painted as its raw code (`systemValueCodeLabel`). */
export const enumCodeFilterOptions = (t_, domain, labelKeys = {}) =>
  enumCodesOf(domain).map((code) => ({ value: code, label: systemValueCodeLabel(t_, domain, code, labelKeys) }))

/**
 * P17: the label of a dropdown option that stands for a loaded row (`edit.optionLabel`, `optionLabelTransform`): the row
 * is translated; a value that is not in the loaded list reads as it is, never guessed.
 *
 * @param {function(string, Object=): string} t_
 * @param {string} domain
 * @param {Array<Object>} items the rows the dropdown loaded
 * @param {string} name the option, the row's `name`
 * @returns {string}
 */
export const systemValueOptionText = (t_, domain, items, name) => {
  const item = (Array.isArray(items) ? items : []).find((candidate) => candidate && candidate.name === name)
  return item ? systemValueLabel(t_, domain, item) : (name ?? '')
}
